import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Fade,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
} from "@chakra-ui/react";
import { FaChevronDown } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { languages } from '../data/languages';
import { getMenuTheme, getRichTextSx } from '../utils/theme';

const hasImage = (url) => typeof url === "string" && url.trim() !== "";

const LocationMenu = () => {
  const { locationId } = useParams();
  const [selectedLang, setSelectedLang] = useState(languages[1]);
  const [menus, setMenus] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Caches fetched menus per location+language so switching back to an
  // already loaded language doesn't hit the backend again.
  const menusCacheRef = useRef(new Map());

  // Refs to each section's AccordionItem, keyed by section id, so the
  // newly expanded section can be smooth-scrolled into view.
  const sectionRefs = useRef({});

  useEffect(() => {
    const cacheKey = `${locationId}/${selectedLang.code}`;
    const cached = menusCacheRef.current.get(cacheKey);
    if (cached) {
      setMenus(cached);
      return;
    }

    fetch(`https://masterpiece-prod-csftcwdvg2ecfmfr.westeurope-01.azurewebsites.net/api/menus/location/${locationId}/language/${selectedLang.code}`)
      .then((res) => res.json())
      .then((data) => {
        menusCacheRef.current.set(cacheKey, data);
        setMenus(data);
      })
      .catch((err) => {
        console.error('Failed to fetch menu:', err);
      });
  }, [selectedLang, locationId]);

  const activeMenu = Array.isArray(menus) ? menus[activeTab] : undefined;
  const pageTheme = getMenuTheme(activeMenu?.colorScheme);

  return (
    <Box bg={pageTheme.appBg} minH="100vh" py={{ base: 4, md: 8 }}>
      <Box maxW={{ base: "7xl", md: "2xl" }} mx="auto" px={{ base: 4, md: 8 }}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6}>
          
          {hasImage(menus?.[0]?.locationLogo) && (
            <Image src={menus[0].locationLogo} alt="Logo" height="3em" />
          )}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<FaChevronDown />}
              variant="outline"
              size="sm"
              px={3}
              py={1.5}
              borderRadius="md"
            >
              <HStack spacing={2}>
                <Image
                  src={selectedLang.flag}
                  alt={selectedLang.label}
                  boxSize="20px"
                  borderRadius="full"
                  userSelect="none"
                />
                <Text fontWeight="medium" userSelect="none">{selectedLang.label}</Text>
              </HStack>
            </MenuButton>
            <MenuList>
              {Array.isArray(languages) && languages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => setSelectedLang(languages.find(l => l.code === lang.code))}
                  _focus={{ bg: 'teal.100' }}
                >
                  <HStack spacing={3}>
                    <Image
                      src={lang.flag}
                      alt={lang.label}
                      boxSize="20px"
                      borderRadius="full"
                    />
                    <Text>{lang.label}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        {/* Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled">
          <Box overflowX="auto">
            <TabList display="flex" gap={2} pb={2} borderBottom="1px solid" borderColor={pageTheme.border}>
              {Array.isArray(menus) && menus.map((menu) => {
                const tabTheme = getMenuTheme(menu.colorScheme);
                return (
                  <Tab
                    key={menu.id}
                    px={{ base: 3, md: 6 }}
                    py={{ base: 2, md: 3 }}
                    borderRadius="md"
                    whiteSpace="nowrap"
                    fontWeight="medium"
                    fontSize={{ base: "sm", md: "md" }}
                    _selected={{
                      bg: tabTheme.primary,
                      color: tabTheme.onPrimary,
                      fontWeight: "bold",
                      boxShadow: "md"
                    }}
                  >
                    {menu.name}
                  </Tab>
                );
              })}
            </TabList>
          </Box>

          <TabPanels>
            {Array.isArray(menus) && menus.map((menu) => {
              const menuTheme = getMenuTheme(menu.colorScheme);
              return (
              <TabPanel key={menu.id} px={0}>
                {Array.isArray(menu.sections) && menu.sections.length === 0 && (
                  <Text textAlign="center" fontSize="lg" color={menuTheme.textSecondary} userSelect="none">
                    Nema dostupnih sekcija.
                  </Text>
                )}

                <Accordion
                  allowToggle
                  onChange={(expandedIndex) => {
                    if (typeof expandedIndex !== "number" || expandedIndex === -1) return;
                    const sectionId = menu.sections?.[expandedIndex]?.id;
                    const el = sectionRefs.current[sectionId];
                    if (el) {
                      setTimeout(() => {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 300);
                    }
                  }}
                >
                  {Array.isArray(menu.sections) && menu.sections.map((section) => (
                    <Fade in key={section.id}>
                      <AccordionItem
                        ref={(el) => (sectionRefs.current[section.id] = el)}
                        border="none"
                        borderRadius="lg"
                        overflow="hidden"
                        boxShadow="lg"
                        bg={menuTheme.cardBg}
                        mb={6}
                        _hover={{ boxShadow: "xl" }}
                      >
                        <AccordionButton _expanded={{ bg: menuTheme.primaryLight }} px={4} py={5}>
                          <HStack
                            flex="1"
                            spacing={4}
                            justify="space-between"
                            align="center"
                          >
                            <HStack spacing={4} align="start" alignItems={section.price !== null && section.price !== 0.00 ? "start" : "center"}>
                              {hasImage(section.image) && (
                                <Image
                                  src={section.image}
                                  alt={section.name}
                                  boxSize={{ base: "64px", md: "96px" }}
                                  borderRadius="md"
                                  objectFit="cover"
                                  flexShrink={0}
                                />
                              )}
                              <Box textAlign="left">
                                <Heading size="md" userSelect="none" mb={1} color={menuTheme.textPrimary}>
                                  {section.name}
                                </Heading>
                                {section.price != 0.00 && section.price !== 0 && (
                                  <Text
                                    fontWeight="bold"
                                    fontSize="md"
                                    color={menuTheme.primary}
                                    whiteSpace="nowrap"
                                    userSelect="none"
                                    mr={3}
                                  >
                                    {Number(section.price).toFixed(2)} EUR
                                  </Text>
                                )}
                              </Box>
                            </HStack>

                            <AccordionIcon />
                          </HStack>
                        </AccordionButton>

                        <AccordionPanel pb={4} px={6}>
                          <VStack
                            align="start"
                            spacing={4}
                            w="100%"
                            divider={<Divider borderColor={menuTheme.border} />}
                          >
                            {Array.isArray(section.items) && section.items.map((item) => (
                              <Box key={item.id} w="100%">
                                <HStack justify="space-between" align="start" spacing={3}>
                                  <Text fontWeight="semibold" fontSize="md" userSelect="none" color={menuTheme.textPrimary} flex="1">
                                    {item.name}
                                  </Text>
                                  {section.price === 0.00 && item.price && (
                                    <Text fontWeight="bold" fontSize="md" color={menuTheme.textPrimary} userSelect="none" whiteSpace="nowrap" flexShrink={0}>
                                      {Number(item.price).toFixed(2)} {item.currency}
                                    </Text>
                                  )}
                                </HStack>
                                {item.description?.trim() && (
                                  <Text fontSize="sm" color={menuTheme.textMuted} mt={1} userSelect="none">
                                    {item.description}
                                  </Text>
                                )}
                                {item.ingredients?.trim() && (
                                  <Text fontSize="sm" color={menuTheme.textSecondary} mt={1} fontStyle="italic" userSelect="none">
                                    {item.ingredients}
                                  </Text>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Fade>
                  ))}
                </Accordion>

                {menu != undefined && menu.description?.trim() && (
                  <Box mt={10} px={[4, 6]}>
                    <Text fontStyle="italic" fontSize="md" color={menuTheme.textSecondary} textAlign="center">
                      {menu?.description || ""}
                    </Text>
                  </Box>
                )}

                {menu.specialNotes != null && menu.specialNotes.trim() !== "" && (
                  <Box
                    mt={8}
                    p={6}
                    bg={menuTheme.cardBg}
                    borderRadius="lg"
                    boxShadow="md"
                    sx={getRichTextSx(menuTheme)}
                    dangerouslySetInnerHTML={{ __html: menu.specialNotes }}
                  />
                )}
              </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>

        {/* Footer */}
        <Box mt={16} pt={{ base: 8, md: 12 }} borderTop="1px solid" borderColor={pageTheme.border}>
          <VStack spacing={{ base: 4, md: 6 }}>
            <Text fontSize={{ base: "sm", md: "md" }} color={pageTheme.textSecondary} userSelect="none">
              {menus?.[0]?.locationName || ""}
            </Text>
            <Text fontSize={{ base: "xs", md: "sm" }} color={pageTheme.textMuted} textAlign="center" px={{ base: 4, md: 16 }} userSelect="none">
              It is prohibited serving or allowing consumption of alcoholic beverages by persons under 18 years.
            </Text>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default LocationMenu;
